/**
 * VÉRAULT Member Suite — Experience · Travel Prep (Wave 28.1 · Phase 5).
 * ────────────────────────────────────────────────────────────────────
 * The calm preparation intelligence layer. Adapts contextually based on
 * destination, trip style, weather, activities, dress codes — and
 * fully respects the destination veil so masked trips never leak
 * location through prep copy.
 *
 *   Four sections:
 *     1. Packing Assistant — never called "AI". Quietly intelligent.
 *     2. Before You Go — passport · visa · arrival · climate
 *     3. Destination Notes — editorial dining/customs/tipping copy
 *     4. Curated Guides — cinematic mini-journals (editorial overlay)
 *
 *   Outstanding Items surface inline at the top, mirroring Essentials.
 */
import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Backpack, ListChecks, Globe2, BookOpen,
  Check, ChevronDown, ArrowUpRight, Clock,
} from "lucide-react";
import { EditorialRule, motion as portalMotion, api } from "../../../index";
import { useArrivalFocus } from "../../../../hooks/useArrivalFocus";
import OutstandingItemsBand from "./editorial/OutstandingItemsBand";
import CuratedGuideOverlay from "./editorial/CuratedGuideOverlay";

const { fadeInUp, stagger } = portalMotion;

// ─── Wave 32 · Travel Prep Pages — editorial reader ──────────────────
function TravelPrepPagesShelf({ pages }) {
  const [activeId, setActiveId] = useState(null);
  const active = pages.find((p) => p.id === activeId) || null;

  return (
    <section className="space-y-4" data-testid="travel-prep-pages-shelf">
      <div className="space-y-1">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
          Travel Prep
        </p>
        <h2 className="font-serif text-[22px] sm:text-[26px] leading-tight">
          Your private guide to this trip
        </h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {pages.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setActiveId(p.id)}
            className="group text-left rounded-md overflow-hidden border border-[rgba(0,0,0,0.06)] hover:border-[hsl(var(--accent-gold))]/40 transition-all bg-white/60"
            data-testid={`travel-prep-page-card-${p.id}`}
          >
            <div className="relative aspect-[16/9] bg-[hsl(var(--background-stone))] overflow-hidden">
              {p.hero_image_url ? (
                <img
                  src={p.hero_image_url}
                  alt={p.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]"
                />
              ) : (
                // Wave 32 · Phase E — refined no-hero fallback. Uses a
                // warm editorial gradient with the page title typeset
                // over it so a freshly-published page never feels empty.
                <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--accent-gold))]/15 via-[hsl(var(--background-paper))] to-[hsl(var(--background-stone))] flex items-end p-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
                    Travel prep · {p.subtitle ? "Editorial" : "Guide"}
                  </p>
                </div>
              )}
            </div>
            <div className="p-4 space-y-1.5">
              <p className="font-serif text-[17px] sm:text-[19px] leading-tight">{p.title}</p>
              {p.subtitle && (
                <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))] line-clamp-2">{p.subtitle}</p>
              )}
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] pt-1">
                Open guide
                <ArrowUpRight className="inline-block size-3 ml-1" />
              </p>
            </div>
          </button>
        ))}
      </div>
      <AnimatePresence>
        {active && (
          <TravelPrepPageReader page={active} onClose={() => setActiveId(null)} />
        )}
      </AnimatePresence>
    </section>
  );
}

function TravelPrepPageReader({ page, onClose }) {
  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onEsc);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  // Wave 33 — folder-aware layout. When the page has visible groups,
  // render folder cards first; clicking opens that folder's sections.
  // Sections are accordion-style (one open at a time), default collapsed.
  const groups = (page.groups || []).filter((g) => !g.hidden);
  const ungrouped = (page.sections || []).filter((s) => !s.group_key);
  const sectionsByGroup = (page.sections || []).reduce((acc, s) => {
    if (s.group_key) {
      (acc[s.group_key] = acc[s.group_key] || []).push(s);
    }
    return acc;
  }, {});
  const [activeFolder, setActiveFolder] = useState(null);
  const showFolderGrid = groups.length > 0 && activeFolder === null;
  const activeGroup = groups.find((g) => g.key === activeFolder) || null;
  const sectionsToShow = activeGroup
    ? sectionsByGroup[activeGroup.key] || []
    : groups.length === 0
    ? page.sections || []
    : ungrouped;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="fixed inset-0 z-50 bg-[hsl(var(--background-paper))] overflow-y-auto"
      data-testid={`travel-prep-page-reader-${page.id}`}
    >
      <button
        type="button"
        onClick={onClose}
        className="fixed top-4 right-4 z-10 size-9 grid place-items-center rounded-full bg-white shadow-md hover:scale-105 transition-transform"
        data-testid="travel-prep-page-reader-close"
        aria-label="Close"
      >
        <span className="text-xl leading-none">×</span>
      </button>
      {page.hero_image_url ? (
        <div className="relative aspect-[16/9] sm:aspect-[21/9] bg-[hsl(var(--background-stone))] overflow-hidden">
          <img src={page.hero_image_url} alt={page.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--background-paper))]/95 via-transparent to-transparent" />
        </div>
      ) : (
        <div className="relative aspect-[16/9] sm:aspect-[21/9] bg-gradient-to-br from-[hsl(var(--accent-gold))]/20 via-[hsl(var(--background-paper))] to-[hsl(var(--background-stone))] flex items-end">
          <div className="w-full px-5 sm:px-12 pb-8 sm:pb-10">
            <p className="font-mono text-[10.5px] uppercase tracking-[0.30em] text-[hsl(var(--foreground-muted))]">
              Travel prep · Private guide
            </p>
          </div>
        </div>
      )}
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-16 space-y-8">
        <header className="space-y-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Travel Prep</p>
          <h1 className="font-serif text-[28px] sm:text-[40px] leading-tight tracking-tight">{page.title}</h1>
          {page.subtitle && (
            <p className="font-accent italic text-[15px] sm:text-[17px] text-[hsl(var(--foreground-secondary))]">{page.subtitle}</p>
          )}
        </header>

        {showFolderGrid ? (
          <FolderGrid
            groups={groups}
            counts={Object.fromEntries(groups.map((g) => [g.key, (sectionsByGroup[g.key] || []).length]))}
            ungroupedCount={ungrouped.length}
            onPickGroup={(k) => setActiveFolder(k)}
            onPickUngrouped={ungrouped.length > 0 ? () => setActiveFolder("__ungrouped__") : null}
          />
        ) : (
          <>
            {groups.length > 0 && (
              <button
                type="button"
                onClick={() => setActiveFolder(null)}
                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] transition-colors"
                data-testid="travel-prep-page-reader-back-to-folders"
              >
                ← Folders
              </button>
            )}
            {activeGroup && (
              <p className="font-serif text-[22px] sm:text-[26px] leading-tight tracking-tight pt-1">
                {activeGroup.label}
              </p>
            )}
            <Accordion sections={sectionsToShow} />
          </>
        )}
      </div>
    </motion.div>
  );
}

// Wave 33 — Folder grid (Arrivals / Departures).
function FolderGrid({ groups, counts, ungroupedCount, onPickGroup, onPickUngrouped }) {
  return (
    <div className="space-y-4" data-testid="travel-prep-folder-grid">
      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
        Choose a folder
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {groups.map((g) => (
          <button
            key={g.key}
            type="button"
            onClick={() => onPickGroup(g.key)}
            className="group text-left rounded-md overflow-hidden border border-[rgba(0,0,0,0.06)] hover:border-[hsl(var(--accent-gold))]/40 bg-white/70 transition-all p-5 sm:p-6"
            data-testid={`travel-prep-folder-card-${g.key}`}
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
              Folder · {counts[g.key] || 0} {(counts[g.key] || 0) === 1 ? "note" : "notes"}
            </p>
            <p className="mt-2 font-serif text-[22px] sm:text-[26px] leading-tight tracking-tight">{g.label}</p>
            <p className="mt-3 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] group-hover:text-[hsl(var(--accent-gold))] transition-colors">
              Open folder
              <ArrowUpRight className="size-3" strokeWidth={1.5} />
            </p>
          </button>
        ))}
        {onPickUngrouped && (
          <button
            type="button"
            onClick={onPickUngrouped}
            className="group text-left rounded-md overflow-hidden border border-dashed border-[rgba(0,0,0,0.10)] hover:border-[hsl(var(--accent-gold))]/40 bg-white/40 transition-all p-5 sm:p-6"
            data-testid="travel-prep-folder-card-ungrouped"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
              Other · {ungroupedCount} {ungroupedCount === 1 ? "note" : "notes"}
            </p>
            <p className="mt-2 font-serif text-[20px] sm:text-[22px] leading-tight tracking-tight text-[hsl(var(--foreground-secondary))]">General notes</p>
          </button>
        )}
      </div>
    </div>
  );
}

// Wave 33 — Accordion shell. Sections default collapsed; only one open
// at a time. The client can still close the open one to collapse all.
function Accordion({ sections }) {
  const [openId, setOpenId] = useState(null);
  if (!sections || sections.length === 0) {
    return (
      <p className="font-accent italic text-[14px] text-[hsl(var(--foreground-muted))]">
        Nothing here yet — your advisor is gathering the notes for this folder.
      </p>
    );
  }
  return (
    <div className="space-y-3">
      {sections.map((s) => (
        <PageSection
          key={s.id}
          section={s}
          open={openId === s.id}
          onToggle={() => setOpenId((curr) => (curr === s.id ? null : s.id))}
        />
      ))}
    </div>
  );
}

function PageSection({ section: s, open, onToggle }) {
  // Wave 33 — accordion-controlled. Parent owns the "one open at a time"
  // state. We always start collapsed regardless of `collapsed_by_default`.
  const collapsed = !open;
  const lines = (s.body || "").split(/\r?\n/);
  const attachments = s.attachments || [];
  // Inline markdown — **bold** and [label](url) become real elements so
  // fuel/pharmacy map links are tappable.
  const renderInline = (text) => {
    const out = [];
    const re = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*]+)\*\*/g;
    let last = 0; let m; let k = 0;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) out.push(text.slice(last, m.index));
      if (m[1]) {
        out.push(
          <a key={k++} href={m[2]} target="_blank" rel="noreferrer noopener"
             className="underline decoration-[hsl(var(--accent-gold))]/50 underline-offset-2 hover:text-[hsl(var(--accent-gold))] transition-colors">
            {m[1]}
          </a>
        );
      } else {
        out.push(<strong key={k++} className="font-semibold text-[hsl(var(--foreground))]">{m[3]}</strong>);
      }
      last = re.lastIndex;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
  };
  return (
    <section
      className="rounded-md bg-white/70 backdrop-blur-[2px] border border-[rgba(0,0,0,0.06)] overflow-hidden"
      data-testid={`travel-prep-page-reader-section-${s.id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-baseline justify-between gap-3 text-left px-5 py-4 sm:px-6 hover:bg-[hsl(var(--background-stone))]/40 transition-colors"
        aria-expanded={!collapsed}
        data-testid={`travel-prep-page-reader-section-toggle-${s.id}`}
      >
        <div className="flex-1 min-w-0">
          {s.featured_reminder && (
            <span className="inline-flex items-center gap-1.5 font-mono text-[9.5px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] border border-[hsl(var(--accent-gold))]/30 mb-2">
              ✦ Featured · {s.reminder_text || "Important"}
            </span>
          )}
          <h2 className="font-serif text-[18px] sm:text-[22px] leading-tight">{s.title}</h2>
        </div>
        <ChevronDown className={`size-4 shrink-0 transition-transform duration-500 mt-1.5 ${collapsed ? "" : "rotate-180"}`} strokeWidth={1.5} />
      </button>
      {!collapsed && (
        <div className="space-y-3 px-5 sm:px-6 pb-5 sm:pb-6 pt-1">
          {lines.map((line, i) => {
            const t = line.trim();
            if (!t || t === "---") return <div key={i} className="h-1" />;
            if (/^\*\*.+\*\*$/.test(t) && !t.slice(2, -2).includes("**")) {
              return (
                <p key={i} className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] pt-2">
                  {t.replace(/^\*\*|\*\*$/g, "")}
                </p>
              );
            }
            if (/^[-•]\s+/.test(t)) {
              return (
                <p key={i} className="font-serif text-[14.5px] leading-relaxed text-[hsl(var(--foreground-secondary))] flex gap-2">
                  <span aria-hidden className="mt-2 inline-block size-1 rounded-full bg-[hsl(var(--accent-gold))]/70 shrink-0" />
                  <span>{renderInline(t.replace(/^[-•]\s+/, ""))}</span>
                </p>
              );
            }
            return (
              <p key={i} className="font-serif text-[14.5px] leading-relaxed text-[hsl(var(--foreground-secondary))]">
                {renderInline(t)}
              </p>
            );
          })}
          {attachments.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-2 pt-2">
              {attachments.map((a) => (
                <a
                  key={a.id}
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-md border border-[rgba(0,0,0,0.06)] hover:border-[hsl(var(--accent-gold))]/40 bg-white/60 p-3 flex items-center gap-3 transition-colors"
                  data-testid={`travel-prep-page-reader-attachment-${a.id}`}
                >
                  <div className="size-10 rounded bg-[hsl(var(--background-stone))] grid place-items-center shrink-0">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                      {a.kind === "link"
                        ? "link"
                        : ((a.content_type || "").split("/").pop()?.slice(0, 4) || "file")}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-[14px] truncate">{a.filename}</p>
                    <p className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                      {a.kind === "link"
                        ? "Open link"
                        : `${Math.round((a.size || 0) / 1024)} KB · open`}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}


// ─── Packing Shelf ───────────────────────────────────────────────────
function PackingShelf({ items, veiled }) {
  // Locally-stored "checked off" state — quiet packing-list memory.
  // Persists per trip in localStorage so the client can use it during
  // pack-out without re-checking on every visit.
  const tripKey = useOutletContext()?.trip?.id;
  const storageKey = tripKey ? `vt-packed-${tripKey}` : null;
  const [checked, setChecked] = useState(() => {
    if (!storageKey) return {};
    try { return JSON.parse(localStorage.getItem(storageKey) || "{}"); }
    catch { return {}; }
  });
  useEffect(() => {
    if (storageKey) localStorage.setItem(storageKey, JSON.stringify(checked));
  }, [checked, storageKey]);

  const toggleItem = (id) => setChecked((c) => ({ ...c, [id]: !c[id] }));

  const kindMeta = {
    climate: { label: "Climate", tone: "text-[hsl(var(--accent-gold))]" },
    activity: { label: "Activity", tone: "text-[hsl(var(--accent-sage))]" },
    dining: { label: "Dining", tone: "text-[hsl(var(--foreground-secondary))]" },
    practical: { label: "Practical", tone: "text-[hsl(var(--foreground-muted))]" },
  };

  if (!items?.length) return null;

  return (
    <section id="packing" data-testid="travel-prep-packing" className="scroll-mt-24">
      <header className="mb-4">
        <div className="flex items-center gap-2 mb-1.5">
          <Backpack className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
            Packing
          </p>
        </div>
        <h2 className="font-serif text-[22px] sm:text-[26px] leading-tight tracking-tight">
          What we&apos;d quietly bring.
        </h2>
        <p className="mt-2 font-accent italic text-[13.5px] text-[hsl(var(--foreground-muted))]">
          {veiled
            ? "Shaped to your trip — refined as your reveal nears."
            : "Shaped to your destination, climate, and reservations."}
        </p>
      </header>

      <ul className="rounded-md bg-white/65 backdrop-blur-[2px] border border-[rgba(0,0,0,0.06)] divide-y divide-[rgba(0,0,0,0.04)]" data-testid="packing-list">
        {items.map((it) => {
          const meta = kindMeta[it.kind] || kindMeta.practical;
          const isChecked = !!checked[it.id];
          return (
            <li key={it.id} className="px-5 py-3.5 sm:px-6" data-testid={`packing-item-${it.id}`}>
              <button
                type="button"
                onClick={() => toggleItem(it.id)}
                className="w-full flex items-center gap-3 text-left group"
                data-testid={`packing-toggle-${it.id}`}
              >
                <span className={`inline-flex items-center justify-center size-5 rounded-full border transition-colors flex-shrink-0 ${
                  isChecked
                    ? "border-[hsl(var(--accent-sage))] bg-[hsl(var(--accent-sage)/0.18)]"
                    : "border-[hsl(var(--border))] group-hover:border-[hsl(var(--accent-gold)/0.6)]"
                }`}>
                  {isChecked && <Check className="size-3 text-[hsl(var(--accent-sage))]" strokeWidth={2.5} />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className={`block font-serif text-[15px] sm:text-[16px] leading-snug transition-colors ${
                    isChecked
                      ? "text-[hsl(var(--foreground-muted))] line-through decoration-[hsl(var(--foreground-muted)/0.4)]"
                      : "text-[hsl(var(--foreground))]"
                  }`}>
                    {it.text}
                  </span>
                </span>
                <span className={`font-mono text-[9.5px] uppercase tracking-[0.22em] ${meta.tone} flex-shrink-0`}>
                  {meta.label}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ─── Before You Go ───────────────────────────────────────────────────
function BeforeYouGoShelf({ items }) {
  if (!items?.length) return null;
  return (
    <section id="readiness" data-testid="travel-prep-readiness" className="scroll-mt-24">
      <header className="mb-4">
        <div className="flex items-center gap-2 mb-1.5">
          <ListChecks className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
            Before you go
          </p>
        </div>
        <h2 className="font-serif text-[22px] sm:text-[26px] leading-tight tracking-tight">
          The quiet checklist.
        </h2>
      </header>
      <div className="space-y-3" data-testid="readiness-list">
        {items.map((it) => (
          <article
            key={it.id}
            className="rounded-md bg-white/65 backdrop-blur-[2px] border border-[rgba(0,0,0,0.06)] px-5 py-4 sm:px-6 sm:py-5"
            data-testid={`readiness-item-${it.id}`}
            data-severity={it.severity}
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              {it.label}
            </p>
            <p className="mt-1.5 font-serif text-[15.5px] sm:text-[16px] leading-snug text-[hsl(var(--foreground))]">
              {it.body}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}

// ─── Destination Notes ───────────────────────────────────────────────
function DestinationNotesShelf({ notes, veiled }) {
  // Veil-safe: when no notes (because trip is veiled), surface a calm
  // placeholder. NEVER render destination-specific copy from a token.
  if (!notes?.length) {
    return (
      <section id="destination" data-testid="travel-prep-destination" className="scroll-mt-24">
        <header className="mb-4">
          <div className="flex items-center gap-2 mb-1.5">
            <Globe2 className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
              Destination notes
            </p>
          </div>
          <h2 className="font-serif text-[22px] sm:text-[26px] leading-tight tracking-tight">
            Notes arrive with the reveal.
          </h2>
        </header>
        <div className="rounded-md border border-[hsl(var(--border))] bg-white/40 px-6 py-8 text-center" data-testid="destination-veiled-placeholder">
          <p className="font-accent italic text-[14px] text-[hsl(var(--foreground-muted))] max-w-md mx-auto">
            {veiled
              ? "Your destination is still quietly held. As the reveal nears, dining customs, neighborhood pacing, and local notes will appear here."
              : "Destination notes are being curated by your advisor."}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="destination" data-testid="travel-prep-destination" className="scroll-mt-24">
      <header className="mb-4">
        <div className="flex items-center gap-2 mb-1.5">
          <Globe2 className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
            Destination notes
          </p>
        </div>
        <h2 className="font-serif text-[22px] sm:text-[26px] leading-tight tracking-tight">
          A quiet briefing.
        </h2>
      </header>
      <div className="rounded-md bg-white/65 backdrop-blur-[2px] border border-[rgba(0,0,0,0.06)] divide-y divide-[rgba(0,0,0,0.04)]" data-testid="destination-notes-list">
        {notes.map((n) => (
          <p
            key={n.id}
            className="px-5 py-4 sm:px-6 font-serif text-[15px] sm:text-[16px] leading-relaxed text-[hsl(var(--foreground))]"
            data-testid={`destination-note-${n.id}`}
          >
            {n.body}
          </p>
        ))}
      </div>
    </section>
  );
}

// ─── Curated Guides ──────────────────────────────────────────────────
function CuratedGuidesShelf({ guides, onSelect }) {
  if (!guides?.length) return null;
  return (
    <section id="guides" data-testid="travel-prep-guides" className="scroll-mt-24">
      <header className="mb-4">
        <div className="flex items-center gap-2 mb-1.5">
          <BookOpen className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
            Curated guides
          </p>
        </div>
        <h2 className="font-serif text-[22px] sm:text-[26px] leading-tight tracking-tight">
          For the slow reading hours.
        </h2>
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
        {guides.map((g) => (
          <button
            key={g.slug}
            type="button"
            onClick={() => onSelect(g)}
            className="group text-left rounded-md overflow-hidden bg-white/65 backdrop-blur-[2px] border border-[rgba(0,0,0,0.06)] hover:border-[hsl(var(--accent-gold)/0.4)] transition-colors"
            data-testid={`guide-card-${g.slug}`}
          >
            <div className="relative aspect-[16/10] overflow-hidden">
              <img
                src={g.hero_image}
                alt={g.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[800ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                <p className="font-mono text-[9.5px] uppercase tracking-[0.28em] opacity-80">
                  <Clock className="inline size-2.5 mr-1 -translate-y-px" strokeWidth={1.5} />
                  {g.read_minutes} min read
                </p>
              </div>
            </div>
            <div className="px-5 py-4">
              <h3 className="font-serif text-[18px] sm:text-[20px] leading-tight tracking-tight">
                {g.title}
              </h3>
              <p className="mt-1.5 font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))] line-clamp-2">
                {g.subtitle}
              </p>
              <p className="mt-3 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] group-hover:text-[hsl(var(--accent-gold))] transition-colors">
                Read the guide
                <ArrowUpRight className="size-3" strokeWidth={1.5} />
              </p>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

// ─── Main Travel Prep page ───────────────────────────────────────────
export default function ExperienceTravelPrep() {
  const { trip } = useOutletContext() || {};
  useArrivalFocus();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [overlayGuide, setOverlayGuide] = useState(null);

  const fetchPrep = () => {
    if (!trip?.id) return Promise.resolve();
    return api.get(`/client-portal/experiences/${trip.id}/travel-prep`)
      .then((res) => {
        setData(res.data);
        setError(null);
      })
      .catch((err) => setError(err));
  };

  useEffect(() => {
    if (!trip?.id) return undefined;
    fetchPrep();
    // Wave 30 · Phase 7c — subscribe to centralized live-sync events.
    const onPrepSync = () => {
      if (document.visibilityState === "visible") fetchPrep();
    };
    window.addEventListener("vt:sync-changed:travel_prep", onPrepSync);
    const onVis = () => {
      if (document.visibilityState === "visible") fetchPrep();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("vt:sync-changed:travel_prep", onPrepSync);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [trip?.id]);

  // Detect veil state for header tone
  const veiled = data?.veiled || false;
  // Wave 12.c — section visibility from the server.
  const sectionVisibility = data?._section_visibility || {};
  const lockedSections = data?._locked_sections || [];
  const allSectionsLocked = lockedSections.length === 4;
  // Wave 32 · Phase E — Travel Prep landing pages count as content too.
  // The "A quiet preparation" placeholder must hide as soon as the
  // advisor publishes ANY page, even if the 4 stock sections remain
  // locked.
  const hasLandingPages = (data?.landing_pages || []).length > 0;
  const showEmptyPlaceholder = allSectionsLocked && !hasLandingPages;
  const isVisible = (key) => !!sectionVisibility[key];

  if (!trip) return null;

  return (
    <motion.div
      initial="initial"
      animate="animate"
      variants={stagger}
      className="space-y-8 sm:space-y-10 pb-12"
      data-testid="travel-prep-root"
    >
      <motion.div variants={fadeInUp}>
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
          Travel prep
        </p>
        <h1 className="mt-2 font-serif text-[28px] sm:text-[36px] leading-tight tracking-tight">
          {veiled ? "A quiet preparation." : "Before you go."}
        </h1>
        <p className="mt-3 font-accent italic text-[15px] text-[hsl(var(--foreground-secondary))] max-w-2xl">
          {veiled
            ? "Packing, readiness, and editorial notes — adapting quietly to your trip as the reveal nears."
            : "Packing, readiness, destination notes, and curated reading — gathered together so nothing feels rushed."}
        </p>
      </motion.div>

      {/* Outstanding items — same band as Essentials */}
      {data?.outstanding_items?.length > 0 && (
        <motion.div variants={fadeInUp}>
          <OutstandingItemsBand items={data.outstanding_items} compact />
        </motion.div>
      )}

      {/* Wave 32 · Travel Prep Pages — advisor-authored editorial pages.
          Render at the very top so they feel like the primary surface. */}
      {data?.landing_pages?.length > 0 && (
        <motion.div variants={fadeInUp}>
          <TravelPrepPagesShelf pages={data.landing_pages} />
        </motion.div>
      )}

      {/* Loading */}
      {!data && !error && (
        <motion.div variants={fadeInUp} className="space-y-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-md bg-white/40 border border-[rgba(0,0,0,0.05)] h-32 animate-pulse" />
          ))}
        </motion.div>
      )}

      {error && (
        <motion.div variants={fadeInUp} className="rounded-md border border-[hsl(var(--border))] bg-white/50 p-6 text-center" data-testid="travel-prep-error">
          <p className="font-accent italic text-[14px] text-[hsl(var(--foreground-muted))]">
            We couldn&apos;t load your travel prep right now. Please refresh.
          </p>
        </motion.div>
      )}

      {/* Four sections — gated by per-section visibility (Wave 12.c) */}
      {data && !allSectionsLocked && (
        <>
          {isVisible("before_you_go") && (
            <motion.div variants={fadeInUp}>
              <BeforeYouGoShelf items={data.before_you_go} />
            </motion.div>
          )}
          {isVisible("packing") && (
            <>
              {isVisible("before_you_go") && <motion.div variants={fadeInUp}><EditorialRule /></motion.div>}
              <motion.div variants={fadeInUp}>
                <PackingShelf items={data.packing} veiled={veiled} />
              </motion.div>
            </>
          )}
          {isVisible("destination_notes") && (
            <>
              {(isVisible("before_you_go") || isVisible("packing")) && <motion.div variants={fadeInUp}><EditorialRule /></motion.div>}
              <motion.div variants={fadeInUp}>
                <DestinationNotesShelf notes={data.destination_notes} veiled={veiled} />
              </motion.div>
            </>
          )}
          {isVisible("curated_guides") && (
            <>
              {(isVisible("before_you_go") || isVisible("packing") || isVisible("destination_notes")) && <motion.div variants={fadeInUp}><EditorialRule /></motion.div>}
              <motion.div variants={fadeInUp}>
                <CuratedGuidesShelf guides={data.curated_guides} onSelect={setOverlayGuide} />
              </motion.div>
            </>
          )}
        </>
      )}

      {/* Wave 12.c — All sections locked: one calm preparing-state */}
      {data && showEmptyPlaceholder && (
        <motion.div
          variants={fadeInUp}
          className="rounded-md border border-[hsl(var(--border))] bg-white/50 p-10 text-center"
          data-testid="travel-prep-all-locked"
        >
          <p className="font-serif text-[22px] tracking-tight">
            A quiet preparation, taking shape.
          </p>
          <p className="mt-3 font-accent italic text-[14.5px] text-[hsl(var(--foreground-muted))] max-w-md mx-auto">
            Your advisor is gathering the small intelligence that travels with
            you — packing notes, readiness details, destination rituals,
            curated reading. Each will arrive here as it&apos;s ready.
          </p>
        </motion.div>
      )}

      {/* Guide overlay */}
      <AnimatePresence>
        {overlayGuide && (
          <CuratedGuideOverlay
            guide={overlayGuide}
            onClose={() => setOverlayGuide(null)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
